import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * WhodunnitCaseFile — the competitive Whodunnit case-file drawer.
 *
 * Same manila-folder drawer slot as LocalsDossier (which stays non-competitive-only);
 * the parent owns the folder chrome + open state, this renders the dark pages inside.
 * Everything here is derived client-side from data the page already holds — no fetches:
 *   EVIDENCE   — the briefing facts (status.case_file), stamped as they escalate.
 *                The killer gets no briefing; their tab says so instead.
 *   STATEMENTS — every suspect's claims, quoted verbatim, grouped by suspect.
 *   THE BOARD  — arrests, vote outcomes, dice rolls per round (accumulated live;
 *                status.dice only carries the current round, so we hoard history).
 *   NOTES      — free-text scratchpad in localStorage. Survives refresh, dies with
 *                the game. Where the deduction happens.
 *
 * Unseen badge: new evidence facts + new board events since the drawer was last open
 * (mirrors LocalsDossier's onUnseenChange contract).
 */

const PAGE_BG = '#1a1512';
const INK = '#e8ddc8';
const INK_DIM = 'rgba(232,221,200,0.55)';
const MANILA = '#d9ba72';
const RED = '#ef4444';

const MSG_TYPES = new Set(['message', 'public_message', 'human_interjection']);

const notesKey = (gameId, playerId) => `whodunnit_notes_${gameId}_${playerId || 'spec'}`;
const seenKey = (gameId, playerId) => `whodunnit_cf_seen_${gameId}_${playerId || 'spec'}`;

export default function WhodunnitCaseFile({
    gameId,
    playerId,
    players = [],
    actions = [],
    caseFile = null,      // innocents' briefing string ('  ·  ' joined facts); null for killer
    youDidIt = false,     // this viewer IS the killer
    round = 1,            // current competitive round
    dice = null,          // status.dice — { round, rolls: {pid: {value, band_name, emoji}}, revealed_ids }
    yourEvidence = null,  // status.your_evidence — clues YOU bagged investigating [{round, object, emoji, line}]
    witnessQa = null,     // status.your_witness_qa — YOUR witness interviews [{round, witness_name, q, a}]
    challenge = null,     // status.human_challenge — this round's dare {text, points, done, label} (LEGACY: whodunnit games created after 07-31 send null; the bet below replaced it)
    yourBet = null,       // 🎯 status.your_bet (Nina 07-31) — ONE opt-in detective's bet per game {text, word, points, penalty, status: offered|accepted|claimed|missed, round}
    onAcceptBet = null,   // flips the bet card — investigation op 'accept_bet'; acceptance is a public beat, the word stays sealed
    yourSecret = null,    // status.your_secret — {text, emoji, confessed}; null for the killer
    secretsOn = false,    // status.secrets_on — secrets exist this game (killer card gate)
    onConfess = null,     // parent-owned API call (this component stays fetch-free)
    yourMadlib = null,    // 🖋️ status.your_madlib — killer-only: their authored picks
    heat = null,          // 🔥 status.poison.heat — killer-only 0..1 (truth surfaced)
    culpritNoun = null,   // scenario pack reskin ("the fridge bandit"); null = classic
    victim = null,        // 🕯️ scenario.victim {name, emoji, age, obituary, last_seen} — the case header
    visible = false,
    onUnseenChange = null,
}) {
    const [tab, setTab] = useState('evidence');
    // 📇 S1 index cards (WHODUNNIT_COPY_AUDIT §S1, Nina go 08-12 — DECISIONS
    // item 29): interviews collapse to the question (tap for the answer),
    // statements show last-3 per suspect with "+N more".
    const [openInterviews, setOpenInterviews] = useState(() => new Set());
    const [openSuspects, setOpenSuspects] = useState(() => new Set());
    const toggleSet = (setter, key) => setter(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    // Headline splitter: break at the first natural boundary (comma/colon/dash)
    // when it lands early enough to read as a headline; else first 4 words.
    const splitFact = (fact) => {
        const m = fact.match(/^(.{8,45}?)(?:,|:| — | – | - )\s*(.+)$/);
        if (m) return { head: m[1], rest: m[2] };
        const words = fact.split(/\s+/);
        if (words.length <= 5) return { head: fact, rest: '' };
        return { head: words.slice(0, 4).join(' '), rest: words.slice(4).join(' ') };
    };
    // 🎯 One-shot guard while the accept_bet op is in flight (double-tap safe
    // server-side too, but the button shouldn't flicker back to offered).
    const [betTaking, setBetTaking] = useState(false);

    // 🤫 Confess flow — two-tap arm so a stray tap can't broadcast your shame.
    const [confessArm, setConfessArm] = useState(false);
    const [confessBusy, setConfessBusy] = useState(false);
    const doConfess = async () => {
        if (!confessArm) {
            setConfessArm(true);
            setTimeout(() => setConfessArm(false), 4000);
            return;
        }
        if (!onConfess || confessBusy) return;
        setConfessBusy(true);
        try { await onConfess(); } finally { setConfessBusy(false); setConfessArm(false); }
    };

    // ---- EVIDENCE ----
    const evidence = useMemo(
        () => (caseFile ? caseFile.split('·').map(s => s.trim()).filter(Boolean) : []),
        [caseFile]
    );

    // ---- STATEMENTS + BOARD (one chronological walk) ----
    const { statements, boardEvents } = useMemo(() => {
        const stmts = {}; // name -> [{ message, round }]
        const board = []; // [{ round, text, kind }]
        let walkRound = 1;
        for (const a of actions) {
            const d = a?.data || {};
            if (a?.type === 'game_announcement') {
                const m = typeof d.message === 'string' && d.message.match(/^Round (\d+)\b/);
                if (m) {
                    walkRound = parseInt(m[1], 10) || walkRound;
                    // 📋 Round beat (Nina 07-26): the Board was pure vote record,
                    // which left the tab EMPTY for all of round 1 — seed it with
                    // the case's own rhythm so the first tap always finds ink.
                    board.push({ round: walkRound, kind: 'beat', text: `🔍 Round ${walkRound} opened. Everyone is a suspect.` });
                }
                if (d.vote_results && d.eliminated_player) {
                    board.push({ round: walkRound, kind: 'arrest', text: `🚔 ${d.eliminated_player} taken to holding` });
                }
                if (d.final_rankings) {
                    board.push({ round: walkRound, kind: 'closed', text: '⚖️ CASE CLOSED' });
                }
                continue;
            }
            if (a?.type === 'vote_cast' && d.voter_name && d.target_name) {
                board.push({ round: walkRound, kind: 'vote', text: `☝️ ${d.voter_name} pointed at ${d.target_name}` });
                continue;
            }
            if (MSG_TYPES.has(a?.type) && d.sender_name && d.message && !d.is_moderator) {
                (stmts[d.sender_name] = stmts[d.sender_name] || []).push({ message: d.message, round: walkRound });
            }
        }
        return { statements: stmts, boardEvents: board };
    }, [actions]);

    // ---- DICE HISTORY (status.dice only carries the current round — accumulate) ----
    // Persisted per game (Nina 07-26): the ref alone lost past rounds on
    // reload, so the Board's roll ledger vanished mid-case. localStorage
    // keeps what THIS viewer saw; a dice.round LOWER than the stored max
    // means the space auto-reset into a fresh case — wipe the old ledger.
    const diceHistoryRef = useRef(null); // round -> { pid: roll }
    const diceStoreKey = `whodunnit_dice_${gameId}`;
    if (diceHistoryRef.current === null) {
        try { diceHistoryRef.current = JSON.parse(localStorage.getItem(diceStoreKey) || '{}') || {}; }
        catch (e) { diceHistoryRef.current = {}; }
    }
    if (dice?.round && dice.rolls && Array.isArray(dice.revealed_ids)) {
        const storedRounds = Object.keys(diceHistoryRef.current).map(Number);
        if (storedRounds.length && dice.round < Math.max(...storedRounds)) {
            diceHistoryRef.current = {}; // fresh case in a reused game id
        }
        const bucket = (diceHistoryRef.current[dice.round] = diceHistoryRef.current[dice.round] || {});
        let diceChanged = false;
        for (const pid of dice.revealed_ids) {
            if (dice.rolls[pid] && !bucket[pid]) { bucket[pid] = dice.rolls[pid]; diceChanged = true; }
        }
        if (diceChanged) {
            try { localStorage.setItem(diceStoreKey, JSON.stringify(diceHistoryRef.current)); } catch (e) { /* private mode */ }
        }
    }
    const nameFor = pid => {
        const p = players.find(u => u.game_player_id === pid || u.id === pid);
        return p?.name || 'Unknown suspect';
    };

    // ---- NOTES ----
    const [notes, setNotes] = useState(() => {
        try { return localStorage.getItem(notesKey(gameId, playerId)) || ''; } catch { return ''; }
    });
    const saveNotes = v => {
        setNotes(v);
        try { localStorage.setItem(notesKey(gameId, playerId), v); } catch { /* private mode */ }
    };

    // ---- INVESTIGATION HAUL (STAGE 1 finds — everyone investigates, killer included) ----
    const bagged = Array.isArray(yourEvidence) ? yourEvidence : [];
    const interviews = Array.isArray(witnessQa) ? witnessQa : [];

    // ---- 🔦 CHALLENGES (spec pillar 3: nothing vanishes) ----
    // status only carries the CURRENT round's dare — hoard the history here
    // (localStorage, like NOTES) so every round's challenge stays re-readable.
    const chKey = `whodunnit_ch_${gameId}_${playerId || 'spec'}`;
    const [challengeLog, setChallengeLog] = useState(() => {
        try { return JSON.parse(localStorage.getItem(chKey)) || []; } catch { return []; }
    });
    useEffect(() => {
        if (!challenge?.text) return;
        setChallengeLog(prev => {
            const i = prev.findIndex(c => c.text === challenge.text);
            if (i !== -1) {
                if (!!prev[i].done === !!challenge.done) return prev;
                const next = prev.slice();
                next[i] = { ...next[i], done: !!challenge.done };
                try { localStorage.setItem(chKey, JSON.stringify(next)); } catch { /* private mode */ }
                return next;
            }
            const next = [...prev, { round, text: challenge.text, points: challenge.points || 5, label: challenge.label || null, done: !!challenge.done }];
            try { localStorage.setItem(chKey, JSON.stringify(next)); } catch { /* private mode */ }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [challenge?.text, challenge?.done, round]);

    // ---- UNSEEN BADGE (evidence + bagged finds + interviews + board + challenges) ----
    const unseenTotals = evidence.length + boardEvents.length + bagged.length + interviews.length + challengeLog.length
        + (yourSecret ? 1 : 0) // your secret landing should light the drawer
        + (yourBet ? 1 : 0); // the dealt bet card should too — it's opt-in, it has to be found
    useEffect(() => {
        if (!onUnseenChange) return;
        let seen = { total: 0 };
        try { seen = JSON.parse(localStorage.getItem(seenKey(gameId, playerId))) || seen; } catch { /* noop */ }
        if (visible) {
            try { localStorage.setItem(seenKey(gameId, playerId), JSON.stringify({ total: unseenTotals })); } catch { /* noop */ }
            onUnseenChange(0);
        } else {
            onUnseenChange(Math.max(0, unseenTotals - (seen.total || 0)));
        }
    }, [visible, unseenTotals, gameId, playerId, onUnseenChange]);

    const suspectNames = Object.keys(statements).sort((x, y) => statements[y].length - statements[x].length);
    const diceRounds = Object.keys(diceHistoryRef.current).map(Number).sort((x, y) => x - y);

    const tabBtn = (id, label) => (
        <button
            key={id}
            onClick={() => setTab(id)}
            style={{
                flex: 1, padding: '6px 2px', cursor: 'pointer',
                background: tab === id ? PAGE_BG : 'rgba(0,0,0,0.25)',
                color: tab === id ? MANILA : INK_DIM,
                border: 'none', borderBottom: tab === id ? `2px solid ${MANILA}` : '2px solid transparent',
                fontWeight: 800, fontSize: '0.62em', letterSpacing: '0.12em',
            }}
        >{label}</button>
    );

    const sectionLabel = text => (
        <div style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.16em', color: INK_DIM, margin: '10px 0 4px' }}>
            {text}
        </div>
    );

    return (
        <div style={{ background: PAGE_BG, borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 220, flex: '1 0 auto' }}>
            <div style={{ display: 'flex' }}>
                {tabBtn('evidence', 'EVIDENCE')}
                {tabBtn('statements', 'STATEMENTS')}
                {tabBtn('board', 'BOARD')}
                {tabBtn('challenges', yourBet ? 'THE BET' : 'DARES')}
                {tabBtn('notes', 'NOTES')}
            </div>

            <div style={{ padding: '10px 12px 14px', overflowY: 'auto', maxHeight: '48dvh', color: INK, fontSize: '0.82em', lineHeight: 1.45 }}>
                {tab === 'evidence' && (
                    <div>
                        {/* 🕯️ THE VICTIM (Nina 07-30) — who this case is ABOUT, top of the
                            file. The authored victim was invisible until the verdict
                            poster; now the file leads with them. */}
                        {victim?.name && (
                            <div style={{
                                marginBottom: 12, padding: '9px 10px', borderRadius: 8,
                                background: 'rgba(217,186,114,0.07)',
                                border: '1px solid rgba(217,186,114,0.35)',
                            }}>
                                <div style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.16em', color: MANILA }}>
                                    🕯️ THE VICTIM
                                </div>
                                <div style={{ marginTop: 5, color: INK, fontWeight: 700 }}>
                                    {victim.emoji ? `${victim.emoji} ` : ''}{victim.name}{victim.age ? ` · ${victim.age}` : ''}
                                </div>
                                {victim.last_seen && (
                                    <div style={{ marginTop: 4, color: INK_DIM, fontSize: '0.85em' }}>
                                        Last seen: {victim.last_seen}
                                    </div>
                                )}
                                {victim.obituary && (
                                    <div style={{ marginTop: 4, color: INK_DIM, fontSize: '0.85em', fontStyle: 'italic' }}>
                                        {victim.obituary}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* 🤫 YOUR SECRET (killer-mode spec §5.1) — the viewer's own dirty
                            laundry, top of the file. Innocents can COME CLEAN (public,
                            system-stamped, proves innocence, costs chips); the killer gets
                            the one card they can never play. */}
                        {(yourSecret || (youDidIt && secretsOn)) && (
                            <div style={{
                                marginBottom: 12, padding: '9px 10px', borderRadius: 8,
                                background: yourSecret?.confessed ? 'rgba(74,222,128,0.06)' : 'rgba(239,68,68,0.07)',
                                border: `1px dashed ${yourSecret?.confessed ? 'rgba(74,222,128,0.5)' : 'rgba(239,68,68,0.45)'}`,
                            }}>
                                <div style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.16em', color: yourSecret?.confessed ? '#4ade80' : RED }}>
                                    🤫 YOUR SECRET · YOURS ALONE
                                </div>
                                {youDidIt ? (
                                    <div style={{ marginTop: 5, color: RED, fontWeight: 700 }}>
                                        Your secret is the murder.
                                        <div style={{ color: INK_DIM, fontWeight: 400, marginTop: 4, fontSize: '0.85em' }}>
                                            Everyone else can come clean. You never can.
                                        </div>
                                        {/* 🖋️ Your authored crime (killer mode §1) — the picks the
                                            whole case derives from. Yours to remember, never to say. */}
                                        {yourMadlib && (
                                            <div style={{ marginTop: 8, color: INK, fontWeight: 400, fontStyle: 'italic', fontSize: '0.9em', borderTop: '1px dashed rgba(239,68,68,0.3)', paddingTop: 7 }}>
                                                🖋️ “{(yourMadlib.line || 'I did it with ___ in ___, and like an idiot I left ___ behind.')
                                                    .replace('___', yourMadlib.weapon)
                                                    .replace('___', yourMadlib.place)
                                                    .replace('___', yourMadlib.left_behind)}”
                                            </div>
                                        )}
                                        {/* 🔥 Heat — the cigarette burning down (killer mode §2):
                                            how much of the real trail the room has surfaced. */}
                                        {typeof heat === 'number' && (
                                            <div style={{ marginTop: 8 }}>
                                                <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.14em', color: heat >= 0.67 ? RED : INK_DIM }}>
                                                    🚬 HEAT · {Math.round(heat * 100)}% of the trail surfaced
                                                </div>
                                                <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', marginTop: 4, overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${Math.round(heat * 100)}%`, borderRadius: 2, background: heat >= 0.67 ? RED : MANILA, transition: 'width 0.6s ease' }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ marginTop: 5 }}>
                                            {yourSecret.emoji || '🤫'} “That night, {yourSecret.text}”
                                        </div>
                                        {yourSecret.confessed ? (
                                            <div style={{ marginTop: 6, color: '#4ade80', fontWeight: 800, fontSize: '0.8em' }}>
                                                📜 CONFESSED. Your alibi is clean.
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ color: INK_DIM, fontSize: '0.8em', flex: 1, minWidth: 140 }}>
                                                    Hide it. Cornered? Come clean: proves you're no
                                                    {' '}{(culpritNoun || 'the killer').replace(/^the\s+/i, '')}, costs chips and pride.
                                                </span>
                                                {onConfess && (
                                                    <button
                                                        onClick={doConfess}
                                                        style={{
                                                            padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                                                            border: `1px solid ${RED}`, fontWeight: 900, fontSize: '0.72em',
                                                            letterSpacing: '0.08em', whiteSpace: 'nowrap',
                                                            background: confessArm ? RED : 'transparent',
                                                            color: confessArm ? '#fff' : RED,
                                                        }}
                                                    >
                                                        {confessBusy ? '…' : confessArm ? 'SAY IT. OUT LOUD.' : '📜 COME CLEAN'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        {youDidIt ? (
                            <div>
                                <div style={{ color: RED, fontWeight: 800, letterSpacing: '0.06em' }}>You know what you did.</div>
                                <div style={{ marginTop: 8, color: INK_DIM }}>
                                    They have a briefing you don't. Watch what they treat as fact.
                                </div>
                            </div>
                        ) : evidence.length === 0 ? (
                            <div style={{ color: INK_DIM }}>No briefing yet. The file lands when the case opens.</div>
                        ) : (
                            <div>
                                {sectionLabel(`WHAT WE KNOW · ROUND ${round}`)}
                                {evidence.map((fact, i) => {
                                    // 📇 S1: index-card shape — bold headline, one dim line.
                                    const { head, rest } = splitFact(fact);
                                    return (
                                        <div key={i} style={{
                                            display: 'flex', gap: 8, padding: '7px 9px', marginBottom: 6,
                                            background: 'rgba(217,186,114,0.08)', border: '1px solid rgba(217,186,114,0.25)', borderRadius: 6,
                                        }}>
                                            <span style={{ color: MANILA, fontWeight: 800 }}>{i + 1}.</span>
                                            <span style={{ minWidth: 0, flex: 1 }}>
                                                <span style={{ fontWeight: 800 }}>{head}</span>
                                                {rest && (
                                                    <div style={{ color: INK_DIM, fontSize: '0.88em', marginTop: 2 }}>{rest}</div>
                                                )}
                                            </span>
                                            {i === evidence.length - 1 && evidence.length > 1 && (
                                                <span style={{ marginLeft: 'auto', color: RED, fontSize: '0.7em', fontWeight: 800, alignSelf: 'center' }}>NEW</span>
                                            )}
                                        </div>
                                    );
                                })}
                                <div style={{ color: INK_DIM, fontSize: '0.85em', marginTop: 6 }}>
                                    {(() => { const c = culpritNoun || 'the killer'; return c.charAt(0).toUpperCase() + c.slice(1); })()} never saw this. Watch who over-explains it.
                                </div>
                            </div>
                        )}

                        {/* STAGE 1 haul — clues this viewer bagged walking the scene */}
                        {bagged.length > 0 && (
                            <div>
                                {sectionLabel('WHAT YOU BAGGED')}
                                {bagged.map((c, i) => (
                                    <div key={i} style={{
                                        display: 'flex', gap: 8, padding: '7px 9px', marginBottom: 6, alignItems: 'flex-start',
                                        background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 6,
                                    }}>
                                        <span>{c.emoji || '🔍'}</span>
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ color: MANILA, fontWeight: 700 }}>{c.object}</span>
                                            <span style={{ color: INK_DIM, fontSize: '0.72em', marginLeft: 6 }}>R{c.round}</span>
                                            <div>{c.line}</div>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Witness interviews — what people told THIS viewer, quoted */}
                        {interviews.length > 0 && (
                            <div>
                                {sectionLabel('WITNESS INTERVIEWS')}
                                {interviews.map((t, i) => {
                                    // 📇 S1: collapsed to the question — tap for the answer.
                                    const open = openInterviews.has(i);
                                    return (
                                        <div
                                            key={i}
                                            onClick={() => toggleSet(setOpenInterviews, i)}
                                            style={{ marginBottom: 7, cursor: 'pointer' }}
                                        >
                                            <div style={{ color: INK_DIM, fontSize: '0.72em' }}>
                                                <span style={{ color: MANILA, marginRight: 5 }}>{open ? '▾' : '▸'}</span>
                                                R{t.round} · you asked {t.witness_name}: “{t.q}”
                                            </div>
                                            {open && (
                                                <div style={{ borderLeft: '2px solid rgba(217,186,114,0.35)', paddingLeft: 8, fontStyle: 'italic', marginTop: 3 }}>
                                                    “{t.a}”
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'statements' && (
                    suspectNames.length === 0 ? (
                        <div style={{ color: INK_DIM }}>Nobody has talked yet. They will.</div>
                    ) : suspectNames.map(name => {
                        // 📇 S1: last-3 per suspect, "+N more" expands (cap 14 as before).
                        const all = statements[name].slice(-14);
                        const open = openSuspects.has(name);
                        const shown = open ? all : all.slice(-3);
                        const hidden = all.length - shown.length;
                        return (
                            <div key={name} style={{ marginBottom: 10 }}>
                                {sectionLabel(name.toUpperCase())}
                                {hidden > 0 && (
                                    <button
                                        onClick={() => toggleSet(setOpenSuspects, name)}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 4px',
                                            color: MANILA, fontWeight: 800, fontSize: '0.72em', letterSpacing: '0.08em',
                                        }}
                                    >+{hidden} more ▸</button>
                                )}
                                {open && all.length > 3 && (
                                    <button
                                        onClick={() => toggleSet(setOpenSuspects, name)}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 4px',
                                            color: INK_DIM, fontWeight: 800, fontSize: '0.72em', letterSpacing: '0.08em',
                                        }}
                                    >show less ▾</button>
                                )}
                                {shown.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 4 }}>
                                        <span style={{ color: INK_DIM, fontSize: '0.72em', minWidth: 22, paddingTop: 2 }}>R{s.round}</span>
                                        <span style={{ borderLeft: `2px solid rgba(217,186,114,0.35)`, paddingLeft: 8 }}>“{s.message}”</span>
                                    </div>
                                ))}
                            </div>
                        );
                    })
                )}

                {tab === 'board' && (
                    <div>
                        {boardEvents.length === 0 && diceRounds.length === 0 && (
                            <div style={{ color: INK_DIM }}>Nothing yet. The record starts when the case opens.</div>
                        )}
                        {boardEvents.length > 0 && (
                            <div>
                                {sectionLabel('THE RECORD')}
                                {boardEvents.map((e, i) => (
                                    <div key={i} style={{ marginBottom: 4 }}>
                                        <span style={{ color: INK_DIM, fontSize: '0.72em', marginRight: 7 }}>R{e.round}</span>
                                        <span style={{ color: e.kind === 'arrest' ? RED : INK }}>{e.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {diceRounds.map(r => (
                            <div key={r}>
                                {sectionLabel(`INSTINCT ROLLS · ROUND ${r}`)}
                                {Object.entries(diceHistoryRef.current[r]).map(([pid, roll]) => (
                                    <div key={pid} style={{ marginBottom: 3 }}>
                                        <span style={{ marginRight: 7 }}>{roll.emoji || '🎲'}</span>
                                        <span style={{ fontWeight: 700 }}>{nameFor(pid)}</span>
                                        <span style={{ color: MANILA, fontWeight: 800, margin: '0 7px' }}>{roll.value}</span>
                                        <span style={{ color: INK_DIM, fontSize: '0.78em', letterSpacing: '0.08em' }}>{roll.band_name}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}

                {tab === 'challenges' && (
                    <div>
                        {/* 🎯 THE DETECTIVE'S BET (Nina 07-31) — one per game, opt-in.
                            Flipping the card is PUBLIC (bulletin beat) but the word
                            stays sealed until the round's vote settles it: +points on
                            a hit, the detective takes `penalty` on a miss. */}
                        {yourBet && (() => {
                            const live = yourBet.status === 'accepted' && yourBet.round === round;
                            const settling = yourBet.status === 'accepted' && yourBet.round !== round;
                            const cardTone = yourBet.status === 'claimed' ? 'rgba(74,222,128,0.5)'
                                : yourBet.status === 'missed' ? 'rgba(239,68,68,0.45)' : MANILA;
                            return (
                                <div style={{
                                    padding: '10px 12px', marginBottom: 10, borderRadius: 6,
                                    background: yourBet.status === 'claimed' ? 'rgba(74,222,128,0.08)'
                                        : yourBet.status === 'missed' ? 'rgba(239,68,68,0.08)'
                                        : 'rgba(217,186,114,0.12)',
                                    border: `1px solid ${cardTone}`,
                                }}>
                                    <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.14em', color: MANILA }}>
                                        🎯 THE DETECTIVE'S BET · ONE PER GAME
                                    </div>
                                    {yourBet.status === 'offered' && !betTaking && (<>
                                        <div style={{ marginTop: 5 }}>{yourBet.text}.</div>
                                        <div style={{ marginTop: 3, fontSize: '0.72em', color: INK_DIM }}>
                                            Win: +{yourBet.points}. Miss: the detective takes {yourBet.penalty}.
                                            Lands this round only. The room hears you took it, never the word.
                                        </div>
                                        {onAcceptBet && (
                                            <button
                                                onClick={async () => {
                                                    if (betTaking) return;
                                                    setBetTaking(true);
                                                    try { await onAcceptBet(); } catch { /* server truth on next poll */ }
                                                    setBetTaking(false);
                                                }}
                                                style={{
                                                    marginTop: 8, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
                                                    background: MANILA, color: '#1a1408', border: 'none',
                                                    fontWeight: 800, fontSize: '0.8em', letterSpacing: '0.08em',
                                                }}
                                            >
                                                🎯 TAKE THE BET
                                            </button>
                                        )}
                                    </>)}
                                    {yourBet.status === 'offered' && betTaking && (
                                        <div style={{ marginTop: 5, color: INK_DIM }}>Flipping the card…</div>
                                    )}
                                    {live && (<>
                                        <div style={{ marginTop: 5, fontWeight: 700 }}>LIVE. Work "{yourBet.word}" into a statement.</div>
                                        <div style={{ marginTop: 3, fontSize: '0.72em', color: INK_DIM }}>
                                            +{yourBet.points} if it lands this round. The detective is listening.
                                        </div>
                                    </>)}
                                    {settling && (
                                        <div style={{ marginTop: 5, color: INK_DIM }}>Round {yourBet.round} settles at the vote…</div>
                                    )}
                                    {yourBet.status === 'claimed' && (
                                        <div style={{ marginTop: 5, color: '#4ade80', fontWeight: 800 }}>
                                            ✓ BET WON. "{yourBet.word}" landed. +{yourBet.points}.
                                        </div>
                                    )}
                                    {yourBet.status === 'missed' && (
                                        <div style={{ marginTop: 5, color: RED, fontWeight: 800 }}>
                                            ✗ The detective collected. "{yourBet.word}" never came. -{yourBet.penalty}.
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        {challengeLog.length === 0 ? (
                            !yourBet && <div style={{ color: INK_DIM }}>No dares yet. The detective hands them out each round.</div>
                        ) : (
                            [...challengeLog].reverse().map((c, i) => {
                                const isCurrent = challenge?.text === c.text;
                                return (
                                    <div key={`${c.round}-${i}`} style={{
                                        padding: '8px 10px', marginBottom: 8, borderRadius: 6,
                                        background: isCurrent ? 'rgba(217,186,114,0.12)' : 'rgba(0,0,0,0.25)',
                                        border: `1px solid ${isCurrent ? MANILA : 'rgba(232,221,200,0.15)'}`,
                                    }}>
                                        <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.14em', color: isCurrent ? MANILA : INK_DIM }}>
                                            {c.done ? '✓ CLAIMED' : isCurrent ? '🔦 THIS ROUND' : `ROUND ${c.round}`}
                                            {c.points ? ` · ${c.points} PTS` : ''}
                                        </div>
                                        <div style={{ marginTop: 3, textDecoration: c.done ? 'line-through' : 'none', opacity: c.done ? 0.6 : 1 }}>
                                            {c.text}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
                {tab === 'notes' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ color: INK_DIM, fontSize: '0.85em' }}>Your working theory. Nobody else sees this.</div>
                        <textarea
                            value={notes}
                            onChange={e => saveNotes(e.target.value)}
                            placeholder={'Suspect list. Slips. Who knew too much...'}
                            style={{
                                width: '100%', minHeight: 140, resize: 'vertical', boxSizing: 'border-box',
                                background: 'rgba(217,186,114,0.06)', color: INK,
                                border: '1px solid rgba(217,186,114,0.3)', borderRadius: 6,
                                padding: '8px 10px', fontSize: '1em', lineHeight: 1.5, fontFamily: 'inherit',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
